import { useNavigate } from 'react-router-dom';
import type { Project } from '@/mocks/projects';

interface ProjectCardProps {
  project: Project;
}

const stageConfig: Record<string, { color: string; bg: string; icon: string }> = {
  '规划': { color: 'text-secondary-600', bg: 'bg-secondary-100', icon: 'ri-compasses-line' },
  '执行': { color: 'text-primary-600', bg: 'bg-primary-100', icon: 'ri-play-circle-line' },
  '等待': { color: 'text-amber-600', bg: 'bg-amber-100', icon: 'ri-pause-circle-line' },
  '复盘': { color: 'text-accent-600', bg: 'bg-accent-100', icon: 'ri-refresh-line' },
  '完成': { color: 'text-accent-700', bg: 'bg-accent-100', icon: 'ri-check-double-line' },
};

export default function ProjectCard({ project }: ProjectCardProps) {
  const navigate = useNavigate();
  const stage = stageConfig[project.stage] || stageConfig['规划'];

  return (
    <button
      onClick={() => navigate(`/projects/${project.id}`)}
      className="w-full text-left bg-background-50 rounded-xl px-4 py-4 border border-background-200/60 hover:border-primary-200 transition-all duration-200 cursor-pointer group"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className={`w-6 h-6 rounded-md flex items-center justify-center ${stage.bg} ${stage.color}`}>
            <i className={`${stage.icon} text-xs`}></i>
          </span>
          <span className="text-sm font-semibold text-foreground-800 font-heading">{project.name}</span>
        </div>
        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${stage.bg} ${stage.color}`}>
          {project.stage}
        </span>
      </div>

      {/* Goal */}
      <p className="text-xs text-foreground-500 mb-3 line-clamp-1">{project.goal}</p>

      {/* Progress bar */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <span className="text-[10px] text-foreground-400">进度</span>
          <span className="text-[10px] font-semibold text-foreground-700">{project.progress}%</span>
        </div>
        <div className="w-full h-1.5 bg-background-200 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500 bg-primary-500"
            style={{ width: `${project.progress}%` }}
          ></div>
        </div>
      </div>

      {/* Next step */}
      {project.nextStep && (
        <div className="flex items-start gap-1.5 mt-2 pt-2 border-t border-background-200/50">
          <span className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
            <i className="ri-arrow-right-circle-line text-accent-500 text-xs"></i>
          </span>
          <span className="text-xs text-accent-600 font-medium leading-relaxed">下一步：{project.nextStep}</span>
        </div>
      )}

      {/* Meta */}
      <div className="flex items-center gap-3 mt-3 text-[10px] text-foreground-400">
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-calendar-line"></i>
          </span>
          {project.deadline}
        </span>
        <span className="flex items-center gap-1">
          <span className="w-3 h-3 flex items-center justify-center">
            <i className="ri-task-line"></i>
          </span>
          {project.taskCount} 项待办
        </span>
      </div>
    </button>
  );
}